/**
 * ConversationBadges.tsx — the colour-coded pills for a conversation row (CONV7).
 *
 * Same visual language and WCAG posture as the Leads `LeadStatusBadge`: colour is
 * ALWAYS paired with an i18n text label so state is never conveyed by colour
 * alone (WCAG 1.4.1), tones map to STATIC class literals (no dynamic
 * concatenation, so Tailwind's scanner keeps them), and an out-of-domain value
 * degrades to a neutral pill rather than crashing or mis-colouring.
 *
 * THE LOAD-BEARING RULE (ADR-058 Addendum B): `delivery_status === 'unknown'`
 * means delivery health is NOT YET ASSESSED (the outbox seam lands later). It
 * MUST render as a neutral "not assessed" pill — NEVER the green/healthy `ok`
 * tone. Treating "unknown" as "healthy" would lie to the operator.
 */

import { useTranslation } from 'react-i18next';
import {
  isConsentState,
  isDeliveryStatus,
  isFunnelStage,
  type ConsentState,
  type DeliveryStatus,
  type FunnelStage,
} from '../../api/types';
import { Badge, type BadgeTone } from '../../components/Badge';

type Tone = BadgeTone;

interface PillProps {
  tone: Tone;
  label: string;
  testid: string;
  dataStage?: string;
  dataDelivery?: string;
  dataConsent?: string;
  dataTone?: string;
  dataKnown?: 'true' | 'false';
  className?: string;
}

const Pill = ({
  tone,
  label,
  testid,
  dataStage = undefined,
  dataDelivery = undefined,
  dataConsent = undefined,
  dataTone = undefined,
  dataKnown = undefined,
  className = '',
}: PillProps) => (
  <Badge
    tone={tone}
    data-testid={testid}
    data-stage={dataStage}
    data-delivery={dataDelivery}
    data-consent={dataConsent}
    data-tone={dataTone}
    data-known={dataKnown}
    className={className}
  >
    {label}
  </Badge>
);

// --- Funnel stage (reuses the shared funnelStage.* vocabulary, ADR-057) ------
const FUNNEL_TONE: Record<FunnelStage, Tone> = {
  new: 'neutral',
  in_conversation: 'accent',
  collecting: 'degraded',
  handed_off: 'ok',
  closed: 'down',
  exception: 'unknown',
};

export const FunnelStageBadge = ({ stage }: { stage: string }) => {
  const { t } = useTranslation();
  const known = isFunnelStage(stage);
  const tone: Tone = known ? FUNNEL_TONE[stage] : 'unknown';
  const label = known
    ? t(`funnelStage.${stage}`)
    : t('conversations.badges.unknownValue', { value: stage });
  return (
    <Pill
      tone={tone}
      label={label}
      testid="conv-funnel-badge"
      dataStage={stage}
      dataKnown={known ? 'true' : 'false'}
    />
  );
};

// --- Delivery status ----------------------------------------------------------
// `unknown` is DELIBERATELY neutral, NOT positive — see the file header rule.
const DELIVERY_TONE: Record<DeliveryStatus, Tone> = {
  ok: 'ok',
  pending: 'accent',
  stuck: 'unknown',
  error: 'down',
  unknown: 'neutral',
};

// `data-tone` keeps its pre-Badge vocabulary (existing contract/tests).
const TONE_ATTR: Record<Tone, string> = {
  ok: 'positive',
  down: 'terminal',
  unknown: 'caution',
  degraded: 'active',
  neutral: 'neutral',
  accent: 'info',
};

export const DeliveryBadge = ({ status }: { status: string }) => {
  const { t } = useTranslation();
  const known = isDeliveryStatus(status);
  const tone: Tone = known ? DELIVERY_TONE[status] : 'neutral';
  const label = known
    ? t(`conversations.delivery.${status}`)
    : t('conversations.badges.unknownValue', { value: status });
  return (
    <Pill
      tone={tone}
      label={label}
      testid="conv-delivery-badge"
      dataDelivery={status}
      dataTone={TONE_ATTR[tone]}
      dataKnown={known ? 'true' : 'false'}
    />
  );
};

// --- Consent state ------------------------------------------------------------
const CONSENT_TONE: Record<ConsentState, Tone> = {
  granted: 'ok',
  declined: 'down',
  unknown: 'neutral',
};

export const ConsentBadge = ({ state }: { state: string }) => {
  const { t } = useTranslation();
  const known = isConsentState(state);
  const tone: Tone = known ? CONSENT_TONE[state] : 'neutral';
  const label = known
    ? t(`conversations.consent.${state}`)
    : t('conversations.badges.unknownValue', { value: state });
  return (
    <Pill
      tone={tone}
      label={label}
      testid="conv-consent-badge"
      dataConsent={state}
      dataKnown={known ? 'true' : 'false'}
    />
  );
};

// --- Paused flag — renders ONLY when the thread is paused ---------------------
export const PausedBadge = ({ paused }: { paused: boolean }) => {
  const { t } = useTranslation();
  if (!paused) return null;
  return <Pill tone="unknown" label={t('conversations.paused')} testid="conv-paused-badge" />;
};
